import { FC } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/typedHooks';
import {
  getAccountingPermissionLevel,
  getCustomerPermissionLevel,
  getInvoicePermissionLevel,
  getJobsPermissionLevel,
  getRefundPermissionLevel,
  getReportsPermissionLevel,
  getTeamSettingPermissionLevel,
  getTipAndServicePermissionLevel
} from 'containers/app/util';
import { PermissionSettingKeys, PermissionSettingType } from '../constants';

import { PermissionItem } from '../EditPermissionsModal';
import {
  changePermission,
  selectAccountingSettingsPermissions,
  selectBankingSettingsPermissions,
  selectCustomersSettingsPermissions,
  selectFeesSettingsPermissions,
  selectInvoiceSettingsPermissions,
  selectJobSettingsPermissions,
  selectRefundSettingsPermissions,
  selectReportsSettingsPermissions,
  selectTeamSettingsPermissions
} from '../editPermissionsSlice';
import PermissionDisclosure from './PermissionDisclosure';
import { selectPaymentSystemConfig } from 'containers/app/appSlice';
import { PermissionTypeEnum } from 'containers/app/app.model';
import { isCAorWA } from 'utils/commonUtils';

interface PermissionDisclosureProps {
  className?: string;
}

const PermissionDisclosures: FC<PermissionDisclosureProps> = () => {
  const selectedTeamSettingsPermissions = useAppSelector(selectTeamSettingsPermissions);
  const selectedInvoiceSettingsPermissions = useAppSelector(selectInvoiceSettingsPermissions);
  const selectedFeesSettingsPermissions = useAppSelector(selectFeesSettingsPermissions);
  const selectedRefundSettingsPermissions = useAppSelector(selectRefundSettingsPermissions);
  const selectedCustomersSettingsPermissions = useAppSelector(selectCustomersSettingsPermissions);
  const selectedBankingSettingsPermissions = useAppSelector(selectBankingSettingsPermissions);
  const selectedJobSettingsPermissions = useAppSelector(selectJobSettingsPermissions);
  const selectedAccountingSettingsPermissions = useAppSelector(selectAccountingSettingsPermissions);
  const selectedReportsSettingsPermissions = useAppSelector(selectReportsSettingsPermissions);

  const { paymentSystem } = useAppSelector(selectPaymentSystemConfig);

  const dispatch = useAppDispatch();

  const teamSettingsPermissions: PermissionItem[] = [
    {
      title: 'View team profile details',
      key: 'viewTeamProfile',
      parentKey: 'teamSettingPermissionList',
      rootKey: PermissionTypeEnum.TEAMSETTINGS,
      enabled: selectedTeamSettingsPermissions?.teamSettingPermissionList.viewTeamProfile
    },
    {
      title: 'Edit team profile details',
      parentKey: 'teamSettingPermissionList',
      rootKey: PermissionTypeEnum.TEAMSETTINGS,
      key: 'editTeamProfile',
      enabled: selectedTeamSettingsPermissions?.teamSettingPermissionList.editTeamProfile
    },
    {
      title: 'Invite a new member',
      parentKey: 'teamSettingPermissionList',
      rootKey: PermissionTypeEnum.TEAMSETTINGS,
      key: 'inviteNewMember',
      enabled: selectedTeamSettingsPermissions?.teamSettingPermissionList.inviteNewMember
    },

    {
      title: 'Edit existing members',
      parentKey: 'teamSettingPermissionList',
      rootKey: PermissionTypeEnum.TEAMSETTINGS,
      key: 'editExistingMember',
      enabled: selectedTeamSettingsPermissions?.teamSettingPermissionList.editExistingMember
    },

    {
      title: 'View existing members',
      parentKey: 'teamSettingPermissionList',
      rootKey: PermissionTypeEnum.TEAMSETTINGS,
      key: 'viewExistingMember',
      enabled: selectedTeamSettingsPermissions?.teamSettingPermissionList.viewExistingMember
    }
  ];

  // Invoices and Receipts related permissions
  const invoicePermissionsCA: PermissionItem[] = [
    {
      title: 'Create invoice',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'createInvoice',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.createInvoice
    },

    {
      title: 'View receipts created by self',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewReceiptsBySelf',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewReceiptsBySelf
    },
    {
      title: 'View receipts created by team',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewReceiptsByTeam',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewReceiptsByTeam,
      alertMessage: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewPayoutsByTeam
        ? `This permission cannot be disabled while View payouts by team is enabled. 
          Please disable View payouts by team first.`
        : '',
      showMessageOnEnable: false
    },
    {
      title: 'View invoices by self',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewInvoicesBySelf',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewInvoicesBySelf
    },

    {
      title: 'View invoices by team',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewInvoicesByTeam',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewInvoicesByTeam
    },

    {
      title: 'View payouts by team',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewPayoutsByTeam',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewPayoutsByTeam,
      alertMessage:
        !selectedRefundSettingsPermissions?.refundPermissionList.viewDisputes ||
        !selectedInvoiceSettingsPermissions?.invoicePermissionList.viewReceiptsByTeam
          ? 'Please enable View disputes and View receipts created by team to enable View payouts by team'
          : '',
      showMessageOnEnable: true
    }
  ];

  // Invoices and Receipts related permissions
  const invoicePermissionsLIP: PermissionItem[] = [
    {
      title: 'Create invoice',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'createInvoice',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.createInvoice
    },

    {
      title: 'View receipts created by self',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewReceiptsBySelf',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewReceiptsBySelf
    },
    {
      title: 'View receipts created by team',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewReceiptsByTeam',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewReceiptsByTeam
    },
    {
      title: 'View invoices by self',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewInvoicesBySelf',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewInvoicesBySelf
    },

    {
      title: 'View invoices by team',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewInvoicesByTeam',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewInvoicesByTeam
    },
    {
      title: 'View payouts by self',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewPayoutsBySelf',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewPayoutsBySelf
    },
    {
      title: 'View payouts by team',
      rootKey: PermissionTypeEnum.INVOICE,
      parentKey: 'invoicePermissionList',
      key: 'viewPayoutsByTeam',
      enabled: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewPayoutsByTeam,
      alertMessage:
        !selectedRefundSettingsPermissions?.refundPermissionList.viewDisputes ||
        !selectedInvoiceSettingsPermissions?.invoicePermissionList.viewReceiptsByTeam
          ? 'Please enable View disputes and View receipts created by team to enable View payouts by team'
          : '',
      showMessageOnEnable: true
    }
  ];

  // permissions related to tips and fees
  const feesPermissions: PermissionItem[] = [
    {
      title: 'Manage items and categories',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'manageItemsAndCategories',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.manageItemsAndCategories
    },

    {
      title: 'Manage taxes',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'manageTaxes',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.manageTaxes
    },
    {
      title: 'Manage service charges',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'manageServiceCharges',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.manageServiceCharges
    },

    {
      title: 'View service fee settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'viewServiceFeeSettings',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.viewServiceFeeSettings
    },

    {
      title: 'Edit service fee settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'editServiceFeeSettings',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.editServiceFeeSettings
    },
    {
      title: 'View payment settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'viewTipsSettings',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.viewTipsSettings
    },
    {
      title: 'Edit payment settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'editTipsSettings',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.editTipsSettings
    },

    {
      title: 'View discount',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'viewDiscount',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.viewDiscount
    },

    {
      title: 'Edit discount',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'editDiscount',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.editDiscount
    },
    {
      title: 'View Terms & Conditions settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'viewTnc',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.viewTnc
    },
    {
      title: 'Edit Terms & Conditions settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'editTnc',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.editTnc
    },
    {
      title: 'View Signature settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'viewSignatureSettings',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.viewSignatureSettings
    },
    {
      title: 'Edit Signature settings',
      rootKey: PermissionTypeEnum.FEES,
      parentKey: 'feesPermissionList',
      key: 'editSignatureSettings',
      enabled: selectedFeesSettingsPermissions?.feesPermissionList.editSignatureSettings
    }
  ];

  // permissions related to refunds
  const refundPermissions: PermissionItem[] = [
    {
      title: 'Create refunds on transactions created by self',
      rootKey: PermissionTypeEnum.REFUND,
      parentKey: 'refundPermissionList',
      key: 'createRefundsBySelf',
      enabled: selectedRefundSettingsPermissions?.refundPermissionList.createRefundsBySelf
    },

    {
      title: 'Create refunds on transactions created by team',
      rootKey: PermissionTypeEnum.REFUND,
      parentKey: 'refundPermissionList',
      key: 'createRefundsByTeam',
      enabled: selectedRefundSettingsPermissions?.refundPermissionList.createRefundsByTeam
    },
    {
      title: 'View disputes',
      rootKey: PermissionTypeEnum.REFUND,
      parentKey: 'refundPermissionList',
      key: 'viewDisputes',
      enabled: selectedRefundSettingsPermissions?.refundPermissionList.viewDisputes,
      alertMessage: selectedInvoiceSettingsPermissions?.invoicePermissionList.viewPayoutsByTeam
        ? `This permission cannot be disabled while View payouts by team is enabled. 
          Please disable View payouts by team first.`
        : '',
      showMessageOnEnable: false
    },
    {
      title: 'Respond to disputes',
      rootKey: PermissionTypeEnum.REFUND,
      parentKey: 'refundPermissionList',
      key: 'respondToDisputes',
      enabled: selectedRefundSettingsPermissions?.refundPermissionList.respondToDisputes
    }
  ];

  // permissions related to customers
  const customersPermissions: PermissionItem[] = [
    {
      title: 'Create new customers',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'createNewCustomers',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.createNewCustomers
    },

    {
      title: 'Edit existing customers',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'editExistingCustomers',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.editExistingCustomers
    },
    {
      title: 'View customers created by team',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'viewCustomersCreatedByTeam',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.viewCustomersCreatedByTeam
    },
    {
      title: 'Edit customers created by team',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'editCustomersCreatedByTeam',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.editCustomersCreatedByTeam
    },
    {
      title: 'View cards on file',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'viewCustomerCards',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.viewCustomerCards
    },
    {
      title: 'Add cards on file',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'createCustomerCards',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.createCustomerCards
    },
    {
      title: 'Edit cards on file',
      rootKey: PermissionTypeEnum.CUSTOMERS,
      parentKey: 'customersPermissionList',
      key: 'editCustomerCards',
      enabled: selectedCustomersSettingsPermissions?.customersPermissionList.editCustomerCards
    }
  ];

  // permissions related to banking
  const bankingPermissions: PermissionItem[] = [
    {
      title: 'View account details',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'viewAccountDetails',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.viewAccountDetails
    },
    {
      title: 'Create ACH transfer',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'createACHTransfer',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.createACHTransfer
    },
    {
      title: 'Create payee',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'createPayee',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.createPayee
    },
    {
      title: 'Edit existing payee',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'editExistingPayee',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.editExistingPayee
    },
    {
      title: 'Download statement',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'downloadStatement',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.downloadStatement
    },
    {
      title: 'View card details',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'viewCardDetails',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.viewCardDetails
    },
    {
      title: 'Manage cards',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'manageCards',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.manageCards
    },
    {
      title: 'View cashback details',
      rootKey: PermissionTypeEnum.BANKING,
      parentKey: 'bankingPermissionList',
      key: 'viewCashbackDetails',
      enabled: selectedBankingSettingsPermissions?.bankingPermissionList.viewCashbackDetails
    }
  ];

  // permissions related to jobs
  const jobPermissions: PermissionItem[] = [
    {
      title: 'Create and edit job',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'createAndEditJob',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.createAndEditJob
    },
    {
      title: 'Manage others jobs',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'manageOthersJobs',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.manageOthersJobs
    },
    {
      title: 'Close or mark job as done',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'closeOrMarkJobAsDone',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.closeOrMarkJobAsDone
    },
    {
      title: 'Modify done jobs',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'modifyDoneJobs',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.modifyDoneJobs
    },
    {
      title: 'View job settings',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'viewJobSettings',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.viewJobSettings
    },
    {
      title: 'Edit job settings',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'editJobSettings',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.editJobSettings
    },
    {
      title: 'View job source',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'viewJobSource',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.viewJobSource,
      alertMessage: selectedAccountingSettingsPermissions?.accountingPermissionList.viewJobProfitability
        ? `This permission cannot be disabled while View job profitability 
          is enabled. Please disable View job profitability first.`
        : '',
      showMessageOnEnable: false
    },
    {
      title: 'Edit job source',
      rootKey: PermissionTypeEnum.JOBS,
      parentKey: 'jobsPermissionList',
      key: 'editJobSource',
      enabled: selectedJobSettingsPermissions?.jobsPermissionList.editJobSource,
      alertMessage: selectedAccountingSettingsPermissions?.accountingPermissionList.overrideJobRatesAndCommissions
        ? // eslint-disable-next-line quotes
          `Cannot disable Edit job source while Override jobs' rates and commissions is enabled.`
        : '',
      showMessageOnEnable: false
    }
  ];

  const canEnableJobProfitability =
    selectedAccountingSettingsPermissions?.accountingPermissionList.manageOthersExpenses &&
    selectedAccountingSettingsPermissions?.accountingPermissionList.managePayAdjustments &&
    selectedJobSettingsPermissions?.jobsPermissionList.viewJobSource &&
    selectedAccountingSettingsPermissions?.accountingPermissionList.viewItemCost;

  const canEnableOverrideJobRatesAndCommissions = selectedJobSettingsPermissions.jobsPermissionList.editJobSource;

  // permissions related to accounting
  const accountingPermissions: PermissionItem[] = [
    {
      title: 'View item cost',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'viewItemCost',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.viewItemCost,
      alertMessage: selectedAccountingSettingsPermissions?.accountingPermissionList.viewJobProfitability
        ? `This permission cannot be disabled while View job profitability 
          is enabled. Please disable View job profitability first.`
        : '',
      showMessageOnEnable: false
    },
    {
      title: 'Edit item cost',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'editItemCost',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.editItemCost
    },
    {
      title: 'Create expenses',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'createExpenses',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.createExpenses
    },
    {
      title: 'Manage others expenses',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'manageOthersExpenses',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.manageOthersExpenses,
      alertMessage: selectedAccountingSettingsPermissions?.accountingPermissionList.viewJobProfitability
        ? `This permission cannot be disabled while View job profitability 
          is enabled. Please disable View job profitability first.`
        : '',
      showMessageOnEnable: false
    },
    {
      title: 'View my job commissions and pay',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'viewMyJobCommissionsAndPay',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.viewMyJobCommissionsAndPay
    },
    {
      title: 'Manage pay adjustments',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'managePayAdjustments',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.managePayAdjustments,
      alertMessage: selectedAccountingSettingsPermissions?.accountingPermissionList.viewJobProfitability
        ? `This permission cannot be disabled while View job profitability 
          is enabled. Please disable View job profitability first.`
        : '',
      showMessageOnEnable: false
    },
    {
      title: 'Override job rates and commissions',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'overrideJobRatesAndCommissions',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.overrideJobRatesAndCommissions,
      alertMessage: canEnableOverrideJobRatesAndCommissions
        ? ''
        : // eslint-disable-next-line quotes
          `Please enable Edit job source to enable Override jobs' rates and commissions.`,
      showMessageOnEnable: true
    },
    {
      title: 'View job profitability',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'viewJobProfitability',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.viewJobProfitability,
      alertMessage: canEnableJobProfitability
        ? ''
        : `Please enable Manage others expenses, Manage pay adjustments, View job source, 
          and View line item costs to enable job profitability.`,
      showMessageOnEnable: true
    },
    {
      title: 'View accounting settings',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'viewAccountingSettings',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.viewAccountingSettings
    },
    {
      title: 'Edit accounting settings',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'editAccountingSettings',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.editAccountingSettings
    },
    {
      title: 'Access team accounting',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'accessTeamAccounting',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.accessTeamAccounting
    },
    {
      title: 'Approve and process accounting',
      rootKey: PermissionTypeEnum.ACCOUNTING,
      parentKey: 'accountingPermissionList',
      key: 'approveAndProcessAccounting',
      enabled: selectedAccountingSettingsPermissions?.accountingPermissionList.approveAndProcessAccounting
    }
  ];

  // permissions related to reports
  const reportsPermissions: PermissionItem[] = [
    {
      title: 'View reports',
      rootKey: PermissionTypeEnum.REPORTS,
      parentKey: 'reportsPermissionList',
      key: 'accessReports',
      enabled: selectedReportsSettingsPermissions?.reportsPermissionList.accessReports
    }
  ];

  const onTogglePermission = (permissionItem: PermissionItem) => {
    if (permissionItem.rootKey === PermissionSettingKeys.BANKING) {
      return;
    }
    dispatch(changePermission({ permission: permissionItem, paymentSystem }));
  };

  return (
    <>
      <PermissionDisclosure
        title={PermissionSettingType.TEAM_SETTINGS}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={teamSettingsPermissions}
        secondaryTitle={getTeamSettingPermissionLevel(selectedTeamSettingsPermissions.teamSettingPermissionList)}
      />

      <PermissionDisclosure
        title={PermissionSettingType.INVOICES_AND_RECEIPTS}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={isCAorWA(paymentSystem) ? invoicePermissionsCA : invoicePermissionsLIP}
        secondaryTitle={getInvoicePermissionLevel(
          selectedInvoiceSettingsPermissions.invoicePermissionList,
          paymentSystem
        )}
      />

      <PermissionDisclosure
        title={PermissionSettingType.TIPS_AND_FEES}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={feesPermissions}
        secondaryTitle={getTipAndServicePermissionLevel(selectedFeesSettingsPermissions.feesPermissionList)}
      />

      <PermissionDisclosure
        title={PermissionSettingType.REFUNDS}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={refundPermissions}
        secondaryTitle={getRefundPermissionLevel(selectedRefundSettingsPermissions.refundPermissionList)}
      />
      <PermissionDisclosure
        title={PermissionSettingType.CUSTOMERS}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={customersPermissions}
        secondaryTitle={getCustomerPermissionLevel(selectedCustomersSettingsPermissions.customersPermissionList)}
      />
      {/* <PermissionDisclosure
        className="cursor-not-allowed opacity-50"
        title={PermissionSettingType.BANKING}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={bankingPermissions}
        secondaryTitle={getBankingPermissionLevel(selectedBankingSettingsPermissions.bankingPermissionList)}
      /> */}

      <PermissionDisclosure
        title={PermissionSettingType.JOB}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={jobPermissions}
        secondaryTitle={getJobsPermissionLevel(selectedJobSettingsPermissions.jobsPermissionList)}
      />

      <PermissionDisclosure
        title={PermissionSettingType.ACCOUNTING}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={accountingPermissions}
        secondaryTitle={getAccountingPermissionLevel(selectedAccountingSettingsPermissions.accountingPermissionList)}
      />

      <PermissionDisclosure
        title={PermissionSettingType.REPORTS}
        onTogglePermission={onTogglePermission}
        permissionsSettingsList={reportsPermissions}
        secondaryTitle={getReportsPermissionLevel(selectedReportsSettingsPermissions.reportsPermissionList)}
      />
    </>
  );
};

export default PermissionDisclosures;
